import React, { FC, useMemo, useState, useCallback } from "react"
import styles from "./styles.module.scss"
import { TABS } from "../tabs"
import { Button, CircularProgress, TextField } from "@material-ui/core"
import { SearchListItem } from "../list"
import omit from "lodash.omit"
import { updateCommunity, updateEvent, updateUser } from "../../../../actions"
import { withErrorContext } from "../../../../contexts/errorContext"

const MULTILINE_INPUT_KEYS = ["details"]

interface EditItemModalProps {
  listItem: SearchListItem | null
  tab: TABS
  fetchData: () => void
  hideModal: () => void
  showError?: (errorText: string) => void
}

export const EditItemModal: FC<EditItemModalProps> = withErrorContext(
  ({ listItem = {}, tab, fetchData, hideModal, showError }) => {
    const [editingListItem, setEditingListItem] = useState(omit(listItem, ["id"]))
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const headerTitle = useMemo(() => {
      if (tab === TABS.USERS) {
        return "User editing"
      }
      if (tab === TABS.EVENTS) {
        return "Event editing"
      }
      if (tab === TABS.COMMUNITIES) {
        return "Community editing"
      }
    }, [tab])

    const renderInputs = () => {
      if (!editingListItem) {
        return null
      }

      return Object.entries(editingListItem).map(([key, value]) => {
        return (
          <TextField
            className={styles.modalInput}
            fullWidth
            required
            defaultValue={value}
            label={key}
            onChange={e =>
              setEditingListItem({
                ...editingListItem,
                [key]: e.target.value,
              })
            }
            multiline={MULTILINE_INPUT_KEYS.includes(key)}
            placeholder={key}
            // note: we know this cast is safe because the key came from Object.entries
            value={editingListItem[key as keyof typeof editingListItem]}
            disabled={isLoading}
          />
        )
      })
    }

    const handleClickSave = useCallback(async () => {
      if (!listItem || !listItem.id) {
        return null
      }

      setIsLoading(true)

      try {
        if (tab === TABS.USERS) {
          await updateUser(listItem.id, editingListItem)
        }

        if (tab === TABS.EVENTS) {
          await updateEvent(listItem.id, editingListItem)
        }

        if (tab === TABS.COMMUNITIES) {
          await updateCommunity(listItem.id, editingListItem)
        }
        hideModal()

        await fetchData()
      } catch (err) {
        if (err instanceof Error) {
          showError?.(err.message)
        }
      }
      setIsLoading(false)
    }, [editingListItem, tab, listItem])

    return (
      <div className={styles.modalBody}>
        <h2>{headerTitle}</h2>
        {renderInputs()}

        <div className="button-container">
          {isLoading ? (
            <CircularProgress size={30} />
          ) : (
            <Button variant="contained" size="small" onClick={handleClickSave}>
              Save
            </Button>
          )}
        </div>
      </div>
    )
  },
)
